import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Plus, Upload, Tag, ExternalLink, Trash2, Download, Sparkles } from 'lucide-react';
import CatalogGrid from '../components/CatalogGrid';
import LabelingTab from '../components/LabelingTab';
import CatalogoGestionTab from '../components/CatalogoGestionTab';
import GuiasTallasTab from '../components/GuiasTallasTab';
import PromocionesTab from '../components/PromocionesTab';
import CampanasTab from '../components/CampanasTab';
import DescuentosTab from '../components/DescuentosTab';
import ProductPage from './ProductPage';
import ImportExcelModal from '../components/ImportExcelModal';
import { QRModal } from '../components/QRCodeDisplay';
import { exportCatalogToExcel } from '../utils/exportExcel';
import { decodeHtml } from '../utils/decodeHtml';
import { compareProductsByCategory, categoriaPrincipal } from '../utils/productSort';
import useViewMode from '../utils/useViewMode';
import { propsSoloLectura } from '../utils/soloLectura';
import PageHeader from '../components/ui/PageHeader';
import EspejoFranja from '../components/EspejoFranja';
import PanelMasivaOptimizacion from '../components/PanelMasivaOptimizacion';
import useMasivaOptimizacion from '../utils/useMasivaOptimizacion';
import PrepararImagenesTab from '../components/PrepararImagenesTab';
import HeaderUtilities from '../components/ui/HeaderUtilities';
import SearchToolbar from '../components/ui/SearchToolbar';
import FilterChips from '../components/ui/FilterChips';
import ViewToggle from '../components/ui/ViewToggle';
import FilterSelect from '../components/ui/FilterSelect';
import DataTable from '../components/ui/DataTable';
import EntityInspector from '../components/ui/EntityInspector';
import Button from '../components/ui/Button';
import DialogoDecision from '../components/ui/DialogoDecision';
import StatusBadge from '../components/ui/StatusBadge';
import { productPlaceholder } from '../utils/productPlaceholder';
import { useToast } from '../components/ToastManager';
import { estadoWooLabel } from '../utils/productFormHelpers';
import { tiposConfigurados } from '../utils/tiposProducto';
import { deleteProductToWoo, fetchTrash, listarMarcas, listarColecciones, descargarFotosLote, slugProducto, fotosPendientesProducto } from '../utils/productApi';
import { colToday } from '../utils/colTimeUtils';
import { listDrafts, deleteDraft } from '../utils/productDraftApi';
import { confirmacionDeEliminar } from '../utils/eliminarProducto';
import FichaBarra from '../components/FichaBarra';
import './CatalogPage.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const SIZE_ATTR_NAMES = new Set(['talla', 'talle', 'size', 'tamaño', 'talles']);
const PLACEHOLDER = productPlaceholder(64, 64);
const BLOQUE = 30; // "Cargar más" fijo, la página de Pedidos (§8.1, F4.5)

function normalizeText(value) {
  return String(value || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
}

function isLow(p, threshold) {
  if (p.variations?.length > 0) return p.variations.some((v) => v.stock_quantity > 0 && v.stock_quantity <= threshold);
  return p.stock_quantity > 0 && p.stock_quantity <= threshold;
}
function isOut(p) {
  if (p.variations?.length > 0) return p.variations.some((v) => (v.stock_quantity || 0) === 0);
  return p.stock_quantity === 0 || p.stock_status === 'outofstock';
}

function unitPrice(item) { return parseFloat(item?.sale_price || item?.price || item?.regular_price) || 0; }
function fmtMoney(v) { const n = parseFloat(v) || 0; return n > 0 ? `$${n.toLocaleString('es-CO', { maximumFractionDigits: 0 })}` : '—'; }
function priceLabel(p) {
  const vars = p.variations || [];
  if (!vars.length) return fmtMoney(unitPrice(p));
  const prices = vars.map(unitPrice).filter((x) => x > 0);
  if (!prices.length) return '—';
  const min = Math.min(...prices), max = Math.max(...prices);
  return min === max ? fmtMoney(min) : `${fmtMoney(min)}–${fmtMoney(max)}`;
}
function totalStock(p) {
  const vars = p.variations || [];
  if (vars.length) return vars.reduce((s, v) => s + (v.stock_quantity || 0), 0);
  return p.stock_quantity || 0;
}
function sizesOf(p) {
  const set = new Set();
  for (const v of p.variations || []) for (const a of v.attributes || []) {
    if (SIZE_ATTR_NAMES.has(a.name?.toLowerCase()) && a.option) set.add(a.option);
  }
  return [...set];
}

// Badge de publicación (local — no acopla a otro módulo). Tanda 3b (defecto
// 1c): los dos tipos de borrador se distinguen con su etiqueta completa.
export function PublishBadge({ status }) {
  if (status === 'draft') return <StatusBadge variant="neutral">Borrador en WooCommerce</StatusBadge>;
  if (status === 'local') return <StatusBadge variant="neutral">Borrador local</StatusBadge>;
  if (status === 'private') return <StatusBadge variant="neutral">Privado</StatusBadge>;
  if (status === 'pending') return <StatusBadge variant="warning">Pendiente</StatusBadge>;
  if (status === 'trash') return <StatusBadge variant="warning">En papelera</StatusBadge>; // tanda 4 (PARTE C)
  if (status === 'future') return <StatusBadge variant="info">Programada</StatusBadge>; // P16: publicación agendada, nunca «Publicado»
  return <StatusBadge variant="success">Publicado</StatusBadge>;
}

function StockBadge({ p, threshold }) {
  if (isOut(p)) return <StatusBadge variant="error">Agotado</StatusBadge>;
  if (isLow(p, threshold)) return <StatusBadge variant="warning">Bajo</StatusBadge>;
  return <span className="cat-stock-ok">{totalStock(p)} und</span>;
}

// ── Inspector de producto — EntityInspector DRAWER plano (el molde). El clic
// en fila/tarjeta lo abre; "Editar" completo abre la FICHA (ProductPage; el
// ProductFormModal se eliminó en la tanda 7). Etiqueta/QR abre el QRModal
// (capacidad conservada, movida de cada tarjeta al inspector, brief §13.1).
function CatalogProductInspector({ product, threshold, onClose, onPrev, onNext, onEdit, onChangeStatus, publishing, onQrOpen, onDelete }) {
  if (!product) return null;
  const p = product;
  const name = decodeHtml(p.name);
  const vars = p.variations || [];

  return (
    <EntityInspector
      open
      title={name}
      subtitle={p.categories?.length ? p.categories.join(', ') : 'Sin categoría'}
      media={<img className="cat-insp-img" src={p.image || PLACEHOLDER} srcSet={p.imageSrcset || undefined} sizes="320px" alt={name} onError={(e) => { e.target.removeAttribute('srcset'); e.target.src = PLACEHOLDER; }} />}
      status={<><PublishBadge status={p.status} />{p.sku && <span className="cat-sku">SKU: {p.sku}</span>}</>}
      onClose={onClose}
      onPrev={onPrev}
      onNext={onNext}
      actions={(
        <>
          <Button icon={<Tag size={15} />} onClick={() => onQrOpen({
            productId: p.id, variationId: null, productName: name, variationLabel: null,
            sku: p.sku || null, price: unitPrice(p) ? `$${unitPrice(p).toLocaleString('es-CO')}` : null,
          })}>Etiqueta/QR</Button>
          {/* Defecto 2 (3c): en vez del "↑ Publicar" viejo (que escribía por el
              endpoint que pisaba precio/stock), un selector de estado sobre el
              endpoint nuevo de catálogo (solo manda {status}). */}
          <label className="cat-status-sel">
            <span>Estado</span>
            <select
              value={p.status} disabled={publishing === p.id} aria-label="Estado de publicación"
              onChange={(e) => onChangeStatus(p.id, e.target.value)}
            >
              <option value="publish">Publicado</option>
              <option value="draft">Borrador</option>
              <option value="private">Privado</option>
              <option value="pending">Pendiente</option>
            </select>
          </label>
          {/* 3e (ajuste 4): Eliminar junto a Etiqueta/QR, misma confirmación
              compartida que la tarjeta, la fila y la ficha. */}
          <Button icon={<Trash2 size={15} />} onClick={() => onDelete(p)}>Eliminar</Button>
          <Button variant="primary" onClick={() => onEdit(p)}>Editar</Button>
        </>
      )}
    >
      <p className="ui-inspector-section-h">Datos</p>
      <div className="cat-facts">
        <div className="cat-fact"><span>Precio de venta</span><strong>{priceLabel(p)}</strong></div>
        <div className="cat-fact"><span>Categoría</span><strong>{p.categories?.join(', ') || '—'}</strong></div>
        <div className="cat-fact"><span>SKU</span><strong>{p.sku || '—'}</strong></div>
        <div className="cat-fact"><span>Publicación</span><strong><PublishBadge status={p.status} /></strong></div>
        <div className="cat-fact"><span>Stock total</span><strong>{totalStock(p)} und</strong></div>
      </div>
      {p.permalink && p.status !== 'draft' && (
        <a className="cat-insp-link" href={p.permalink} target="_blank" rel="noopener noreferrer">
          Ver en la tienda <ExternalLink size={13} aria-hidden="true" />
        </a>
      )}

      <p className="ui-inspector-section-h">{vars.length ? `Variaciones (${vars.length})` : 'Stock'}</p>
      <div className="cat-varlist">
        {vars.length ? vars.map((v) => {
          const q = v.stock_quantity || 0;
          const st = q === 0 ? 'out' : q <= threshold ? 'low' : 'ok';
          return (
            <div key={v.id} className={`cat-var cat-var--${st}`}>
              <div className="cat-var-info">
                <span className="cat-var-name">{v.label}</span>
                <span className="cat-var-sub">{v.sku ? `SKU: ${v.sku} · ` : ''}{q} und · {fmtMoney(unitPrice(v))}</span>
              </div>
              <button className="cat-var-qr" title={`Etiqueta/QR de ${v.label}`} onClick={() => onQrOpen({
                productId: p.id, variationId: v.id, productName: name, variationLabel: v.label,
                sku: v.sku || null, price: unitPrice(v) ? `$${unitPrice(v).toLocaleString('es-CO')}` : null,
              })}><Tag size={14} aria-hidden="true" /></button>
            </div>
          );
        }) : (
          <div className="cat-var cat-var--ok">
            <div className="cat-var-info">
              <span className="cat-var-name">Producto simple</span>
              <span className="cat-var-sub">{p.sku ? `SKU: ${p.sku} · ` : ''}{totalStock(p)} und · {fmtMoney(unitPrice(p))}</span>
            </div>
          </div>
        )}
      </div>
    </EntityInspector>
  );
}

function CatalogPage({ products, loading, loadingMore = false, error, total, lowStockThreshold = 5, onRefresh, userRole, storeName = '', labelConfig, catalogoConfig = null, plataforma = 'woo', navTab = null, espejoInfo = null, onReleerVisibles = null, onMigrarEspejo = null, soloLectura = false }) {
  const toast = useToast();
  const showToast = useMemo(() => (toast && toast.showToast) || (() => {}), [toast]);
  const [viewMode, setViewMode] = useViewMode('sh.catalog.viewMode'); // 'table' | 'grid' (recordado)
  const [searchQuery, setSearchQuery] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [sizeFilter, setSizeFilter] = useState('');
  // P3-4: filtros por marca y colección (nombres visibles; vacío = todas).
  const [marcaFilter, setMarcaFilter] = useState('');
  const [coleccionFilter, setColeccionFilter] = useState('');
  const [marcasFiltro, setMarcasFiltro] = useState([]);
  const [coleccionesFiltro, setColeccionesFiltro] = useState([]);
  const [stockFilter, setStockFilter] = useState('all');  // 'all' | 'low' | 'none'
  // Tanda 6 (PARTE C): filtro y orden por la columna Ficha + progreso del recálculo.
  const [fichaFilter, setFichaFilter] = useState('all');   // 'all' | 'incompleta'
  const [sort, setSort] = useState(null);                   // { columnId: 'ficha', dir } | null
  const [statusFilter, setStatusFilter] = useState('all');// 'all' | 'publish' | 'draft'
  const [categories, setCategories] = useState([]);
  // W2 (hotfix 22-sep): el «Actualizar» del ENCABEZADO relee los visibles de la
  // tienda (lo que hacía el botón de la franja — dos botones iguales confunden);
  // el progreso y el resultado se muestran en la franja, que quedó informativa.
  const [actualizandoEspejo, setActualizandoEspejo] = useState(false);
  // P3-9b (antesala de la masiva): fotos pendientes de optimizar por producto, del
  // censo guardado (snapshot). Indicador discreto en cada fila.
  const [fotosPendientes, setFotosPendientes] = useState({});
  const [estadoEspejo, setEstadoEspejo] = useState(null);
  const actualizarDesdeTienda = useCallback(async () => {
    const ids = (products || []).slice(0, 24).map((p) => p.id);
    if (!onReleerVisibles || !ids.length) { onRefresh && onRefresh(); return; }
    setActualizandoEspejo(true);
    setEstadoEspejo(`Actualizando los ${ids.length} visibles…`);
    try {
      const r = await onReleerVisibles(ids);
      setEstadoEspejo(r && typeof r.releidos === 'number'
        ? `${r.releidos} actualizados${(r.fallidos || []).length ? ` · ${r.fallidos.length} fallaron` : ''}`
        : null);
    } catch (e) {
      setEstadoEspejo(`No se pudo actualizar: ${e.message}`);
    } finally {
      setActualizandoEspejo(false);
    }
  }, [products, onReleerVisibles, onRefresh]);
  // P2b tanda 1 — editor por ESTADO (estilo Compras, sin React Router):
  // null = listado; { mode:'new' } = ficha nueva; { mode:'edit', id } = edición.
  // CatalogPage no se desmonta mientras el editor está abierto, así que al volver
  // el listado conserva filtros, búsqueda y paginación.
  const [editor, setEditor] = useState(null);
  // Tanda 3b (defecto 1c): borradores locales de Neto (ProductDraft) para el
  // filtro "Borradores". Se recargan al volver del editor (pudo crear/borrar uno).
  const [localDrafts, setLocalDrafts] = useState([]);
  const [showImport, setShowImport] = useState(false);
  const [publishing, setPublishing] = useState(null);
  const [qrModal, setQrModal] = useState(null);
  const [activeTab, setActiveTab] = useState('products'); // 'products' | 'labels'
  const [selectedId, setSelectedId] = useState(null);
  const [visible, setVisible] = useState(BLOQUE);

  useEffect(() => {
    if (navTab?.tab && ['products', 'labels'].includes(navTab.tab)) setActiveTab(navTab.tab);
    // Tanda 7 (PARTE C): "Crear variación" de Inventario llega con recordId —
    // abre la FICHA de ese producto en la sección de variaciones (si es simple,
    // la ficha avisa que primero debe pasarse a variable).
    if (navTab?.tab === 'products' && navTab.recordId != null) {
      setEditor({ mode: 'edit', id: navTab.recordId, focoInicial: 'variaciones' });
    }
  }, [navTab]);

  const recargarCategorias = useCallback(() => {
    fetch(`${API_URL}/api/categories?form=1&nocache=1`)
      .then((r) => (r.ok ? r.json() : []))
      .then((c) => setCategories(Array.isArray(c) ? c : []))
      .catch(() => {});
  }, []);

  useEffect(() => { recargarCategorias(); }, [recargarCategorias]);

  // P3-9b: fotos pendientes de optimizar por producto (admin/coordinador — el
  // endpoint lo exige). Se recarga al cerrar el editor (optimizar cambia el estado,
  // aunque el censo es snapshot). Si falla, el indicador simplemente no aparece.
  useEffect(() => {
    if (editor || (userRole !== 'admin' && userRole !== 'coordinador')) return;
    fotosPendientesProducto().then((d) => setFotosPendientes(d.pendientes || {})).catch(() => {});
  }, [editor, userRole]);

  // P3-4: opciones de los filtros marca/colección (nombres visibles). Se recargan
  // cuando cambia el catálogo (al cerrar el editor) por si se creó una marca nueva.
  useEffect(() => {
    if (editor) return;
    listarMarcas().then((m) => setMarcasFiltro(Array.isArray(m) ? m : [])).catch(() => {});
    listarColecciones().then((c) => setColeccionesFiltro(Array.isArray(c) ? c : [])).catch(() => {});
  }, [editor]);

  useEffect(() => {
    if (editor) return; // con el editor abierto no hace falta; al cerrarse se refresca
    fetch(`${API_URL}/api/product-drafts`)
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setLocalDrafts(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, [editor]);

  // Tanda 4 (PARTE C): la papelera de Catálogo — lista propia (status=trash no
  // viene en `products`) que se refresca al volver del editor y tras cada
  // Restaurar / Eliminar definitivamente.
  const [trashRows, setTrashRows] = useState([]);
  const cargarPapelera = useCallback(() => {
    fetchTrash()
      .then((t) => setTrashRows(t.map((p) => ({
        ...p, isTrash: true, categories: [], variations: [],
        stock_quantity: 0, regular_price: '', permalink: '',
      }))))
      .catch(() => {});
  }, []);
  useEffect(() => { if (!editor) cargarPapelera(); }, [editor, cargarPapelera]);

  // Restaurar / eliminar definitivo desde la papelera, con confirmación.
  const [confirmTrash, setConfirmTrash] = useState(null); // { tipo:'restaurar'|'definitivo', p }
  const confirmarTrash = useCallback(async () => {
    const { tipo, p } = confirmTrash || {};
    setConfirmTrash(null);
    if (!p) return;
    try {
      if (tipo === 'restaurar') {
        const res = await fetch(`${API_URL}/api/catalog/products/${p.id}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: 'draft' }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'No se pudo restaurar el producto');
        showToast({ message: `"${decodeHtml(p.name)}" se restauró como Borrador.`, variant: 'success' });
      } else {
        await deleteProductToWoo(p.id, { definitivo: true });
        showToast({ message: `"${decodeHtml(p.name)}" se eliminó definitivamente.`, variant: 'success' });
      }
      cargarPapelera();
      onRefresh && onRefresh();
    } catch (e) {
      showToast({ message: e.message, variant: 'error' });
    }
  }, [confirmTrash, cargarPapelera, onRefresh, showToast]);

  // Filas del listado para los borradores locales de productos NUEVOS (los de
  // un producto existente ya tienen su fila propia y su banner en la ficha).
  // Defecto 2 (3d): este filtro es lo que garantiza UNA entrada por producto —
  // un borrador ENLAZADO (wcProductId) no duplica la fila del producto de Woo.
  // Lo fija catalogUnaEntrada.test.js (estrenado por mutación: sin el filtro,
  // el mismo nombre aparece 2 veces → rojo).
  const localDraftRows = useMemo(() => (
    localDrafts
      .filter((d) => d.wcProductId == null)
      .map((d) => ({
        id: `local-${d._id}`,
        isLocalDraft: true,
        draftDoc: d,
        name: String(d.payload?.name || '').trim() || '(Borrador sin nombre)',
        sku: d.payload?.sku || '',
        status: 'local',
        categories: [],
        variations: [],
        stock_quantity: parseInt(d.payload?.stockQty, 10) || 0,
        regular_price: d.payload?.regularPrice || '',
        image: null,
        permalink: '',
      }))
  ), [localDrafts]);

  const sizeOptions = useMemo(() => {
    const sizes = new Set();
    for (const p of products) for (const v of p.variations || []) for (const a of v.attributes || []) {
      if (SIZE_ATTR_NAMES.has(a.name?.toLowerCase())) sizes.add(a.option);
    }
    return [...sizes].sort();
  }, [products]);

  // 3e (ajuste 6): contador por ESTADO para los cuatro chips. El de Borradores
  // suma los locales de Neto (que el filtro también muestra).
  const statusCounts = useMemo(() => {
    // P16: `future` = «Programados» (publicación agendada).
    const c = { publish: 0, draft: localDraftRows.length, private: 0, pending: 0, future: 0 };
    for (const p of products) if (c[p.status] != null) c[p.status] += 1;
    return c;
  }, [products, localDraftRows]);

  // SKUs del catálogo local (producto + variaciones) para avisar duplicados en la ficha.
  const editorSkus = useMemo(() => {
    const set = new Set();
    for (const p of products) {
      if (p.sku) set.add(p.sku);
      for (const v of p.variations || []) if (v.sku) set.add(v.sku);
    }
    return [...set];
  }, [products]);

  const filteredProducts = useMemo(() => {
    let result = products;
    const q = normalizeText(searchQuery);
    if (q) {
      result = result.filter((p) =>
        normalizeText(p.name).includes(q) ||
        normalizeText(p.sku).includes(q) ||
        (p.categories || []).some((c) => normalizeText(c).includes(q)) ||
        (p.variations || []).some((v) => normalizeText(v.sku).includes(q) || (v.attributes || []).some((a) => normalizeText(a.option).includes(q)))
      );
    }
    if (statusFilter === 'publish') result = result.filter((p) => p.status === 'publish');
    else if (statusFilter === 'draft') {
      // Tanda 3b (defecto 1c): "Borradores" = borradores de WooCommerce + los
      // borradores locales de Neto (con su etiqueta cada uno).
      const localesFiltrados = q
        ? localDraftRows.filter((p) => normalizeText(p.name).includes(q) || normalizeText(p.sku).includes(q))
        : localDraftRows;
      result = [...result.filter((p) => p.status === 'draft'), ...localesFiltrados];
    } else if (statusFilter === 'private' || statusFilter === 'pending' || statusFilter === 'future') {
      // 3e (ajuste 6): los chips nuevos filtran por su estado exacto. P16: future
      // («Programados») entra por la misma vía.
      result = result.filter((p) => p.status === statusFilter);
    } else if (statusFilter === 'trash') {
      // Tanda 4 (PARTE C): la papelera es su PROPIA lista (no viene en
      // products y por eso jamás cuenta en el total del catálogo).
      result = q
        ? trashRows.filter((p) => normalizeText(p.name).includes(q) || normalizeText(p.sku).includes(q))
        : trashRows;
      return [...result].sort(compareProductsByCategory);
    }
    if (catFilter) result = result.filter((p) => p.categories.includes(catFilter));
    // P3-4: filtro por marca y por colección (ambos por nombre visible).
    if (marcaFilter) result = result.filter((p) => (p.brands || []).includes(marcaFilter));
    if (coleccionFilter) result = result.filter((p) => (p.colecciones || []).includes(coleccionFilter));
    if (sizeFilter) result = result.filter((p) => p.variations?.some((v) => v.attributes?.some((a) => a.option === sizeFilter)));
    if (stockFilter === 'none') result = result.filter(isOut);
    else if (stockFilter === 'low') result = result.filter((p) => isLow(p, lowStockThreshold) && !isOut(p));
    // Tanda 6: "Ficha incompleta" = evaluación materializada con pendientes
    // (un producto SIN evaluación no se afirma incompleto: queda fuera del chip).
    if (fichaFilter === 'incompleta') result = result.filter((p) => p.ficha && p.ficha.cumplidas < p.ficha.aplicables);
    // Orden por la columna Ficha (los sin evaluación al final, en ambos sentidos).
    if (sort && sort.columnId === 'ficha') {
      const clave = (p) => (p.ficha && p.ficha.aplicables ? p.ficha.cumplidas / p.ficha.aplicables : null);
      return [...result].sort((a, b) => {
        const ka = clave(a); const kb = clave(b);
        if (ka == null && kb == null) return 0;
        if (ka == null) return 1;
        if (kb == null) return -1;
        return sort.dir === 'asc' ? ka - kb : kb - ka;
      });
    }
    // Agrupado por categoría A→Z (Sin categoría al final), nombre A→Z dentro —
    // la misma regla que Inventario y Ventas (utils/productSort).
    return [...result].sort(compareProductsByCategory);
  }, [products, localDraftRows, trashRows, searchQuery, catFilter, marcaFilter, coleccionFilter, sizeFilter, stockFilter, statusFilter, fichaFilter, sort, lowStockThreshold]);

  const countByCategory = useMemo(() => {
    const acc = {};
    for (const p of filteredProducts) { const c = categoriaPrincipal(p); acc[c] = (acc[c] || 0) + 1; }
    return acc;
  }, [filteredProducts]);

  useEffect(() => { setVisible(BLOQUE); }, [searchQuery, catFilter, marcaFilter, coleccionFilter, sizeFilter, stockFilter, statusFilter, fichaFilter]);
  const pageProducts = useMemo(() => filteredProducts.slice(0, visible), [filteredProducts, visible]);

  // P3-9a: descargar las fotos de los productos DEL FILTRO actual (un .zip con una
  // carpeta por producto, nombres SEO, sin recomprimir). Tope 50 — filtrar para el resto.
  const [descargandoLote, setDescargandoLote] = useState(false);
  // Nombre base del zip = «<filtro>-fotos-<fecha>»: si hay EXACTAMENTE un filtro de
  // categoría/marca/colección, su slug; si hay varios o ninguno, «catalogo».
  const nombreBaseLote = useCallback(() => {
    const activos = [catFilter, marcaFilter, coleccionFilter].filter(Boolean);
    const filtro = activos.length === 1 ? slugProducto(decodeHtml(activos[0])) : 'catalogo';
    return `${filtro || 'catalogo'}-fotos-${colToday()}`;
  }, [catFilter, marcaFilter, coleccionFilter]);
  const descargarFotosDelFiltro = useCallback(async () => {
    const todos = filteredProducts.map((p) => p.id).filter(Boolean);
    const ids = todos.slice(0, 50);
    if (!ids.length) { showToast({ message: 'No hay productos en el filtro para descargar.', variant: 'warning' }); return; }
    setDescargandoLote(true);
    if (todos.length > 50) showToast({ message: `Se descargan las primeras 50 de ${todos.length}. Filtra más para el resto.`, variant: 'info' });
    try { await descargarFotosLote(ids, nombreBaseLote()); }
    catch (e) { showToast({ message: e.message || 'No se pudieron descargar las fotos', variant: 'error' }); }
    finally { setDescargandoLote(false); }
  }, [filteredProducts, showToast, nombreBaseLote]);

  const hasFilters = searchQuery.trim() || catFilter || marcaFilter || coleccionFilter || sizeFilter || stockFilter !== 'all' || statusFilter !== 'all' || fichaFilter !== 'all';

  const clearFilters = () => { setSearchQuery(''); setCatFilter(''); setMarcaFilter(''); setColeccionFilter(''); setSizeFilter(''); setStockFilter('all'); setStatusFilter('all'); setFichaFilter('all'); };

  // P3-9b (remate): el disparador de la optimización masiva vive en un botón del
  // encabezado; el panel grande solo aparece con un trabajo en curso/pausado/terminado.
  const puedeOptimizar = userRole === 'admin' || userRole === 'coordinador';
  const masiva = useMasivaOptimizacion({
    habilitado: puedeOptimizar,
    hasFilters: !!hasFilters,
    filtroIds: hasFilters ? filteredProducts.map((p) => p.id).filter(Boolean) : null,
    onChange: onRefresh,
  });

  // Defecto 2 (3c): cambiar el estado usa el endpoint NUEVO de catálogo
  // (buildCatalogPayload manda SOLO lo presente en el body → un {status} no pisa
  // precio/stock/nombre; el endpoint viejo /api/products/:id sí lo hacía). Avisa
  // "Estado cambiado a X" o el error real; nunca queda pensando en silencio.
  const handleChangeStatus = useCallback(async (productId, status) => {
    setPublishing(productId);
    try {
      const res = await fetch(`${API_URL}/api/catalog/products/${productId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showToast({ message: data.error || 'No se pudo cambiar el estado.', variant: 'error' });
      } else {
        showToast({ message: `Estado cambiado a ${estadoWooLabel(status)}`, variant: 'success' });
        onRefresh && onRefresh();
      }
    } catch (e) {
      showToast({ message: e.message || 'No se pudo cambiar el estado.', variant: 'error' });
    }
    setPublishing(null);
  }, [onRefresh, showToast]);

  const selected = selectedId ? (products.find((p) => p.id === selectedId) || null) : null;
  const selIdx = selected ? pageProducts.findIndex((p) => p.id === selectedId) : -1;

  // 3e (ajuste 4): Eliminar desde la tarjeta, la fila y el panel — un solo
  // flujo, con la confirmación COMPARTIDA (nombra el producto y dice si va a
  // papelera o borrado definitivo según su estado).
  const [confirmDel, setConfirmDel] = useState(null); // { id, nombre, conf }
  const pedirEliminar = useCallback((p) => {
    if (!p || p.isLocalDraft) return; // un borrador local no está en Woo
    setConfirmDel({ id: p.id, nombre: decodeHtml(p.name), conf: confirmacionDeEliminar(decodeHtml(p.name), p.status) });
  }, []);
  const confirmarEliminar = useCallback(async () => {
    const { id, nombre, conf } = confirmDel || {};
    setConfirmDel(null);
    if (id == null) return;
    try {
      await deleteProductToWoo(id, { definitivo: conf.definitivo });
      // Tanda 4 (PARTE D, residuo de la 3h): los borradores locales ENLAZADOS
      // mueren con el producto también desde el catálogo, igual que en la ficha.
      try {
        const enlazados = await listDrafts({ wcProductId: id });
        for (const d of enlazados || []) { try { await deleteDraft(d._id); } catch { /* ignore */ } }
      } catch { /* sin borradores que limpiar */ }
      showToast({
        message: conf.definitivo ? `"${nombre}" se eliminó definitivamente.` : `"${nombre}" se envió a la papelera.`,
        variant: 'success',
      });
      setSelectedId(null);
      cargarPapelera(); // si fue a papelera, el chip lo refleja ya
      onRefresh && onRefresh();
    } catch (e) {
      showToast({ message: e.message || 'No se pudo eliminar el producto.', variant: 'error' });
    }
  }, [confirmDel, cargarPapelera, onRefresh, showToast]);

  /* S2c#6 — mismo defecto que se acaba de arreglar en Inventario, pero acá ya
     estaba vivo desde antes. `loading` de App se apaga con el lote 1 (24 de 98),
     y a partir de ahí este texto contaba sobre una lista incompleta:

       - Sin filtro, "24 de 98 productos" es honesto: barra de progreso, se lee
         solo como incompleto. Se conserva, pero contando `products` (lo cargado)
         y no `filteredProducts`, para que el numerador diga lo que promete.
       - CON filtro puesto es una TASA, y miente aunque el total esté a la vista:
         "9 de 98" con el filtro Agotados se lee como 8% cuando la verdad puede
         ser 33%. Ahí no hay denominador honesto posible: se dice "calculando…".

     Ya completo, "36 de 98 productos" vuelve a ser cierto en los dos números.
     El ancho: todos los textos nuevos son más cortos que el "1.000 de 1.000
     productos" que reserva el countWidth={142} de S1#4d — no se toca nada. */
  const catalogoIncompleto = loading || loadingMore;
  // Tanda 4 (PARTE C): la papelera no se mezcla con el "N de N" del catálogo.
  const resultText = statusFilter === 'trash'
    ? `${filteredProducts.length} en la papelera`
    : catalogoIncompleto
      ? (hasFilters || !total ? 'calculando…' : `${products.length} de ${total} productos`)
      : `${filteredProducts.length} de ${total} producto${total !== 1 ? 's' : ''}`;

  // Columnas de la tabla (variante rich §7.1) — imagen + nombre + SKU.
  const columns = [
    {
      id: 'producto', header: 'Producto',
      render: (p) => {
        const name = decodeHtml(p.name);
        return (
          <div className="cat-prod-cell">
            {/* P3-9b: srcset + sizes chico → en la celda de la tabla el navegador baja
                la variante pequeña (no el medium_large), nítida sin sobre-descargar. */}
            <img className="cat-prod-img" src={p.image || PLACEHOLDER} srcSet={p.imageSrcset || undefined} sizes="48px" alt="" loading="lazy" onError={(e) => { e.target.removeAttribute('srcset'); e.target.src = PLACEHOLDER; }} />
            <div className="cat-prod-copy">
              <span className="cat-prod-name">
                {p.permalink && p.status !== 'draft'
                  ? <a href={p.permalink} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>{name} <ExternalLink size={12} aria-hidden="true" /></a>
                  : name}
              </span>
              <span className="cat-prod-sub">
                {p.sku && <span>SKU: {p.sku}</span>}
                {p.status !== 'publish' && <PublishBadge status={p.status} />}
                {/* P3-9b: indicador discreto de fotos pendientes de optimizar (censo). */}
                {fotosPendientes[p.id] > 0 && (
                  <span className="cat-fotos-pesadas" title="Según el último censo de fotos. Se optimizan desde la ficha del producto.">
                    {fotosPendientes[p.id]} {fotosPendientes[p.id] === 1 ? 'foto pesada' : 'fotos pesadas'}
                  </span>
                )}
              </span>
            </div>
          </div>
        );
      },
    },
    { id: 'categorias', header: 'Categorías', hideSm: true, render: (p) => (p.categories?.length ? p.categories.join(', ') : <span className="cat-muted">—</span>) },
    {
      id: 'variaciones', header: 'Variaciones', hideSm: true,
      render: (p) => {
        if (!(p.variations || []).length) return <span className="cat-muted">Producto simple</span>;
        const sizes = sizesOf(p);
        return (
          <div className="cat-cell-2l">
            <span className="cat-cell-2l-main">{sizes.length ? sizes.join(', ') : `${p.variations.length} ${p.variations.length === 1 ? 'variación' : 'variaciones'}`}</span>
            {/* "variación" pierde la tilde en plural: se alterna la palabra entera. */}
            <span className="cat-cell-2l-sub">{p.variations.length} {p.variations.length !== 1 ? 'variaciones' : 'variación'}</span>
          </div>
        );
      },
    },
    { id: 'precio', header: 'Precio', align: 'right', render: (p) => priceLabel(p) },
    // Tanda 6 (PARTE C): SOLO lee la evaluación materializada; '—' honesto sin doc.
    { id: 'ficha', header: 'Ficha', hideSm: true, sortable: true, render: (p) => <FichaBarra ficha={p.ficha} /> },
    { id: 'stock', header: 'Stock', align: 'right', render: (p) => <StockBadge p={p} threshold={lowStockThreshold} /> },
    { id: 'estado', header: 'Estado', render: (p) => <PublishBadge status={p.status} /> },
  ];

  // Un borrador local no tiene id de Woo: editar retoma su payload en la ficha nueva.
  const abrirFila = (p) => {
    if (p.isTrash) return; // tanda 4: la papelera no abre inspector — se restaura o se elimina
    if (p.isLocalDraft) setEditor({ mode: 'new', draft: p.draftDoc });
    else setSelectedId(p.id);
  };

  const rowActions = (p) => (p.isTrash
    ? [
      { id: 'restaurar', label: 'Restaurar', onClick: () => setConfirmTrash({ tipo: 'restaurar', p }) },
      { id: 'definitivo', label: 'Eliminar definitivamente', destructive: true, icon: <Trash2 size={14} />, onClick: () => setConfirmTrash({ tipo: 'definitivo', p }) },
    ]
    : p.isLocalDraft
    ? [{ id: 'editar', label: 'Editar', onClick: () => setEditor({ mode: 'new', draft: p.draftDoc }) }]
    : [
      { id: 'editar', label: 'Editar', onClick: () => setEditor({ mode: 'edit', id: p.id }) },
      ...(p.status !== 'publish' ? [{ id: 'publicar', label: 'Publicar', onClick: () => handleChangeStatus(p.id, 'publish') }] : []),
      { id: 'qr', label: 'Etiqueta/QR', onClick: () => setQrModal({ productId: p.id, variationId: null, productName: decodeHtml(p.name), variationLabel: null, sku: p.sku || null, price: unitPrice(p) ? `$${unitPrice(p).toLocaleString('es-CO')}` : null }) },
      // 3e (ajuste 4): Eliminar también desde la fila, misma confirmación.
      { id: 'eliminar', label: 'Eliminar', destructive: true, icon: <Trash2 size={14} />, onClick: () => pedirEliminar(p) },
    ]);

  const onProducts = activeTab === 'products';

  // Editor abierto: reemplaza el listado por la ficha, SIN desmontar CatalogPage
  // (sus estados de filtro/búsqueda/paginación sobreviven al volver).
  if (editor) {
    return (
      <ProductPage
        productId={editor.mode === 'edit' ? editor.id : null}
        categories={categories}
        existingSkus={editorSkus}
        userRole={userRole}
        soloLectura={soloLectura}
        storeName={storeName}
        catalogoConfig={catalogoConfig}
        plataforma={plataforma}
        productosCatalogo={products}
        focoInicial={editor.focoInicial || null}
        onIrAGuias={() => { setEditor(null); setSelectedId(null); setActiveTab('guias'); }}
        initialDraft={editor.draft || null}
        onBack={() => { setEditor(null); setSelectedId(null); }}
        onSaved={() => { onRefresh && onRefresh(); }}
        // Defecto 3 (3d): ante un POST rechazado por SKU duplicado, la ficha
        // resuelve el id del ocupante contra el catálogo CARGADO (best-effort:
        // si aún no llegó en la carga progresiva, no hay botón) y "Abrirlo"
        // salta a su ficha de edición.
        buscarIdPorSku={(sku) => {
          const s = String(sku || '').trim();
          if (!s) return null;
          const p = products.find((x) => x.sku === s || (x.variations || []).some((v) => v.sku === s));
          return p ? p.id : null;
        }}
        onOpenProduct={(id) => setEditor({ mode: 'edit', id })}
      />
    );
  }

  return (
    <div className="page-catalog mobile-safe page-shell">
      {/* P3-11-C (3-nov-2026): el aviso de salud de sincronización YA NO se muestra acá ni en
          ningún módulo (decisión de Miguel). Vive SOLO en Configuración → Sincronización (con
          detalle y fecha) y como un punto discreto en el ícono de actividad del encabezado. Un
          aviso rojo sobre el Catálogo por un webhook callado tras un redeploy era ruido en la
          pantalla de trabajo. */}
      {/* P3-9b (remate): el panel grande de optimización MASIVA aparece SOLO mientras
          hay un trabajo en confirmación/curso/pausado/recién terminado; el disparador
          es el botón «Optimizar fotos» del encabezado. */}
      {puedeOptimizar && <PanelMasivaOptimizacion masiva={masiva} hasFilters={!!hasFilters} />}
      {/* W2 (hotfix 22-sep): la franja es INFORMATIVA — el único «Actualizar»
          es el del encabezado (HeaderUtilities), que relee los visibles. */}
      <EspejoFranja
        espejoInfo={espejoInfo}
        onMigrarEspejo={onMigrarEspejo}
        estado={estadoEspejo}
      />
      <PageHeader
        title="Catálogo"
        description="Productos de tu tienda: publicación, precios, stock y etiquetas."
        tabs={{
          tabs: [
            { id: 'products', label: 'Productos' },
            { id: 'labels', label: 'Etiquetas' },
            // P3-4: gestión de taxonomías — admin/coordinador (escriben en Woo).
            ...(userRole === 'admin' || userRole === 'coordinador' ? [
              { id: 'categorias', label: 'Categorías' },
              { id: 'marcas', label: 'Marcas' },
              { id: 'colecciones', label: 'Colecciones' },
              // P3-8: preparar imágenes no-producto (hero/banner/categoría).
              { id: 'preparar', label: 'Preparar imágenes' },
            ] : []),
            // Tanda 5: guías de tallas (contrato v0.7.156); si el tema Vitrina
            // no está activo la pestaña se muestra deshabilitada con motivo.
            { id: 'guias', label: 'Guías de tallas' },
            // Punto 2 de promociones (15-sep-2026): SOLO admin — la pestaña
            // aplica precios (escritura en Woo por el camino del dashboard).
            ...(userRole === 'admin' ? [{ id: 'promociones', label: 'Promociones' }] : []),
            // Campañas de promoción (tanda 3): pantalla propia, admin (escribe en
            // Woo por el camino del dashboard). Aparte de «Promociones» (sueltas).
            ...(userRole === 'admin' ? [{ id: 'campanas', label: 'Campañas' }] : []),
            // Descuentos (SERIE «Descuentos», T2): colección nueva; previsualización
            // por JOB con progreso real. Admin (el aplicar escribe en Woo — sub-fase 2b).
            ...(userRole === 'admin' ? [{ id: 'descuentos', label: 'Descuentos' }] : []),
          ],
          active: activeTab,
          onChange: setActiveTab,
        }}
        primaryAction={onProducts ? (
          <Button variant="primary" icon={<Plus size={16} />} {...propsSoloLectura(soloLectura)} onClick={() => setEditor({ mode: 'new' })}>Agregar producto</Button>
        ) : null}
        secondaryActions={onProducts ? (
          // Tanda 7c (punto 4): "Recalcular fichas" se fue a Configuración →
          // Catálogo ("Recalcular estado de fichas") — la sincronización ya
          // recalcula sola los productos que cambian.
          <>
            <Button icon={<Download size={14} />} onClick={descargarFotosDelFiltro} disabled={descargandoLote}
              title="Descarga en un .zip las fotos de los productos del filtro actual (una carpeta por producto, nombres SEO)">
              {descargandoLote ? 'Descargando…' : `Descargar fotos (${filteredProducts.length})`}
            </Button>
            {puedeOptimizar && (
              <Button icon={<Sparkles size={14} />} onClick={masiva.pedirConfirmacion} disabled={masiva.ocupado || masiva.activo}
                title="Optimiza en segundo plano las fotos pesadas de los productos del filtro actual (o de todo el catálogo). Las ya óptimas se saltan.">
                {masiva.activo ? 'Optimizando…' : 'Optimizar fotos'}
              </Button>
            )}
            <Button icon={<Upload size={14} />} onClick={() => setShowImport(true)}>Importar Excel</Button>
          </>
        ) : null}
        utilities={onProducts ? (
          <HeaderUtilities
            onRefresh={actualizarDesdeTienda}
            refreshing={loading || actualizandoEspejo}
            onExport={products.length > 0 ? () => exportCatalogToExcel(products, undefined, tiposConfigurados(catalogoConfig)) : null}
            exportDisabled={loading || products.length === 0}
          />
        ) : null}
      />

      {activeTab === 'labels' && (
        <LabelingTab products={products} storeName={storeName} labelConfig={labelConfig} />
      )}

      {activeTab === 'categorias' && (userRole === 'admin' || userRole === 'coordinador') && (
        <CatalogoGestionTab tipo="categorias" soloLectura={soloLectura} onCambio={recargarCategorias} />
      )}
      {activeTab === 'marcas' && (userRole === 'admin' || userRole === 'coordinador') && (
        <CatalogoGestionTab tipo="marcas" soloLectura={soloLectura} />
      )}
      {activeTab === 'colecciones' && (userRole === 'admin' || userRole === 'coordinador') && (
        <CatalogoGestionTab tipo="colecciones" soloLectura={soloLectura} />
      )}

      {activeTab === 'preparar' && (userRole === 'admin' || userRole === 'coordinador') && (
        <PrepararImagenesTab soloLectura={soloLectura} />
      )}

      {activeTab === 'guias' && (
        <GuiasTallasTab categories={categories} catalogoConfig={catalogoConfig} />
      )}

      {activeTab === 'promociones' && userRole === 'admin' && (
        <PromocionesTab soloLectura={soloLectura} />
      )}

      {activeTab === 'campanas' && userRole === 'admin' && (
        <CampanasTab soloLectura={soloLectura} />
      )}

      {activeTab === 'descuentos' && userRole === 'admin' && (
        <DescuentosTab soloLectura={soloLectura} />
      )}

      {onProducts && (<>
        <SearchToolbar
          search={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder="Nombre, SKU, categoría o variación…"
          resultText={resultText}
          /* S1#4d: reservar el texto MÁS LARGO al que puede llegar la tienda, no
             el de hoy. El cluster de filtros es flex-grow:1 y se come todo el
             espacio libre, así que la toolbar no tiene holgura (medido S1: 0px
             en 1440/1280/1024/768) y este número ES el ancho que les queda a los
             filtros: si se reserva de menos, los filtros nacen anchos y se
             encogen al llegar el dato. 142px = "1.000 de 1.000 productos"
             (141.84 medido). Hoy muestra "98 de 98" (107.20) y con 108 productos
             pasa a "108 de 108" (121.23) — o sea que 112 ya vencía.
             Techo medido antes de romper: 1024 es el más ajustado con 178px. */
          countWidth={142}
          trailing={<ViewToggle value={viewMode} onChange={setViewMode} />}
        >
          <select value={catFilter} onChange={(e) => setCatFilter(e.target.value)} aria-label="Categoría">
            <option value="">Todas las categorías</option>
            {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
          </select>
          {/* P3-4: marca y colección — sólo aparecen si la tienda tiene alguna. */}
          {marcasFiltro.length > 0 && (
            <select value={marcaFilter} onChange={(e) => setMarcaFilter(e.target.value)} aria-label="Marca">
              <option value="">Todas las marcas</option>
              {marcasFiltro.map((m) => <option key={m.id} value={m.name}>{m.name}</option>)}
            </select>
          )}
          {coleccionesFiltro.length > 0 && (
            <select value={coleccionFilter} onChange={(e) => setColeccionFilter(e.target.value)} aria-label="Colección">
              <option value="">Todas las colecciones</option>
              {coleccionesFiltro.map((c) => <option key={c.id} value={c.nombre}>{c.nombre}</option>)}
            </select>
          )}
          {/* Reserva su espacio mientras cargan las tallas (no crece la toolbar).
              Ancho declarado 141px (S1#2, medido acá): en esta toolbar el
              placeholder largo ("Todas las tallas") ya era la opción más ancha,
              así que este select no se movía. Se declara igual para que siga sin
              moverse cuando la tienda cargue tallas más largas que él.
              Exceptuado del truncado, como todas las tallas. */}
          <FilterSelect
            value={sizeFilter} onChange={setSizeFilter} options={sizeOptions}
            allLabel="Todas las tallas" ariaLabel="Talla" allValue="" loading={loading}
            width={141} noTruncate
          />
        </SearchToolbar>

        {/* Dos ejes de filtro EN chips (F4.5): publicación y stock — nunca un
            solo chip que los mezcle (§7.1) */}
        <div className="cat-chipsbar">
          <FilterChips
            ariaLabel="Estado de publicación"
            options={[
              // 3e (ajuste 6): los cuatro estados, cada uno con su contador.
              { id: 'publish', label: 'Publicados', count: statusCounts.publish },
              { id: 'draft', label: 'Borradores', count: statusCounts.draft },
              { id: 'private', label: 'Privados', count: statusCounts.private },
              { id: 'pending', label: 'Pendientes', count: statusCounts.pending },
              // P16: «Programados» (status future) — publicación agendada; solo el
              // chip aparece siempre, con su conteo.
              { id: 'future', label: 'Programados', count: statusCounts.future },
              // Tanda 4 (PARTE C): lo que está en la papelera, con su contador.
              { id: 'trash', label: 'Papelera', count: trashRows.length },
            ]}
            value={statusFilter === 'all' ? null : statusFilter}
            onChange={(v) => setStatusFilter(v || 'all')}
          />
          <span className="cat-chips-div" aria-hidden="true" />
          <FilterChips
            ariaLabel="Estado de stock"
            options={[
              { id: 'low', label: 'Stock bajo' },
              { id: 'none', label: 'Sin stock' },
            ]}
            value={stockFilter === 'all' ? null : stockFilter}
            onChange={(v) => setStockFilter(v || 'all')}
          />
          <span className="cat-chips-div" aria-hidden="true" />
          {/* Tanda 6: eje propio (nunca mezclado con stock, §7.1). */}
          <FilterChips
            ariaLabel="Estado de la ficha"
            options={[{ id: 'incompleta', label: 'Ficha incompleta' }]}
            value={fichaFilter === 'all' ? null : fichaFilter}
            onChange={(v) => setFichaFilter(v || 'all')}
          />
          {/* 3e (ajuste 6): visible con CUALQUIER filtro o búsqueda activa
              (chip, categoría, talla o texto); limpia todo de una. */}
          {hasFilters && (
            <button type="button" className="cat-clear-chip" onClick={clearFilters}>
              ✕ · Limpiar filtros
            </button>
          )}
        </div>

        {/* Tanda 4 (PARTE C): la nota del SKU ocupado, visible con la papelera. */}
        {statusFilter === 'trash' && (
          <p className="cat-trash-nota">
            Mientras esté aquí su SKU sigue ocupado. Restaura el producto (vuelve como Borrador) o elimínalo definitivamente para liberarlo.
          </p>
        )}

        {error && <div className="status-msg error">{error}</div>}

        {viewMode === 'table' ? (
          <DataTable
            variant="rich"
            columns={columns}
            rows={pageProducts}
            rowKey="id"
            loading={loading}
            onRowClick={abrirFila}
            selectedKey={selectedId}
            rowActions={rowActions}
            rowActionsHideSm
            sort={sort}
            onSortChange={setSort}
            groupBy={sort ? null : (p) => {
              const cat = categoriaPrincipal(p);
              return { label: cat, meta: `${countByCategory[cat] || 0} producto${(countByCategory[cat] || 0) !== 1 ? 's' : ''}` };
            }}
            hasActiveFilters={!!hasFilters}
            onClearFilters={clearFilters}
            empty={{ icon: '🔍', title: hasFilters ? 'Sin resultados' : 'Sin productos', description: hasFilters ? 'Ningún producto coincide con los filtros.' : 'Cuando haya productos cargados aparecerán aquí.' }}
            fit={false}
            pagination={{ mode: 'loadMore', hasMore: filteredProducts.length > visible, loadingMore: false, onLoadMore: () => setVisible((v) => v + BLOQUE) }}
          />
        ) : (
          <>
            <CatalogGrid
              products={pageProducts}
              loading={loading}
              onSelect={abrirFila}
              onDelete={pedirEliminar}
              onRestore={(p) => setConfirmTrash({ tipo: 'restaurar', p })}
              onDeleteForever={(p) => setConfirmTrash({ tipo: 'definitivo', p })}
              selectedId={selectedId}
              threshold={lowStockThreshold}
              hasFilters={hasFilters}
              onClearFilters={clearFilters}
            />
            {filteredProducts.length > visible && (
              <div className="ui-dt-pager ui-dt-pager--center">
                <Button size="sm" onClick={() => setVisible((v) => v + BLOQUE)}>Cargar más</Button>
              </div>
            )}
          </>
        )}

        {!loading && loadingMore && products.length > 0 && (
          <div className="progressive-loading-hint"><span className="spinner spinner-sm" /> Cargando más productos…</div>
        )}

        {selected && (
          <CatalogProductInspector
            product={selected}
            threshold={lowStockThreshold}
            onClose={() => setSelectedId(null)}
            onPrev={selIdx > 0 ? () => setSelectedId(pageProducts[selIdx - 1].id) : null}
            onNext={selIdx >= 0 && selIdx < pageProducts.length - 1 ? () => setSelectedId(pageProducts[selIdx + 1].id) : null}
            onEdit={(p) => setEditor({ mode: 'edit', id: p.id })}
            onChangeStatus={handleChangeStatus}
            publishing={publishing}
            onQrOpen={setQrModal}
            onDelete={pedirEliminar}
          />
        )}

        {confirmDel && (
          <DialogoDecision abierto onClose={() => setConfirmDel(null)} ariaLabel={confirmDel.conf.title}>
            <h3>{confirmDel.conf.title}</h3>
            <p>{confirmDel.conf.message}</p>
            <div className="pp-confirm-actions">
              <Button onClick={() => setConfirmDel(null)}>Volver</Button>
              <Button variant="primary" onClick={confirmarEliminar}>{confirmDel.conf.confirmLabel}</Button>
            </div>
          </DialogoDecision>
        )}

        {confirmTrash && (
          <DialogoDecision abierto onClose={() => setConfirmTrash(null)}
            ariaLabel={confirmTrash.tipo === 'restaurar' ? 'Restaurar producto' : 'Eliminar definitivamente'}>
            <h3>{confirmTrash.tipo === 'restaurar' ? 'Restaurar producto' : 'Eliminar definitivamente'}</h3>
            <p>
              {confirmTrash.tipo === 'restaurar'
                ? `Vas a restaurar "${decodeHtml(confirmTrash.p.name)}" desde la papelera: vuelve como Borrador (no se publica solo).`
                : `Vas a eliminar "${decodeHtml(confirmTrash.p.name)}" definitivamente. Esta acción no se puede deshacer; su SKU queda libre.`}
            </p>
            <div className="pp-confirm-actions">
              <Button onClick={() => setConfirmTrash(null)}>Volver</Button>
              <Button variant="primary" onClick={confirmarTrash}>
                {confirmTrash.tipo === 'restaurar' ? 'Restaurar' : 'Eliminar definitivamente'}
              </Button>
            </div>
          </DialogoDecision>
        )}
      </>)}

      {showImport && (
        <ImportExcelModal onClose={() => setShowImport(false)} onSaved={() => { onRefresh && onRefresh(); }} />
      )}

      {qrModal && (
        <QRModal
          open={!!qrModal}
          onClose={() => setQrModal(null)}
          productId={qrModal.productId}
          variationId={qrModal.variationId}
          productName={qrModal.productName}
          variationLabel={qrModal.variationLabel}
          sku={qrModal.sku}
          price={qrModal.price}
          storeName={storeName}
          labelConfig={labelConfig}
        />
      )}
    </div>
  );
}

export default CatalogPage;
